const toPinnedBool = (value) => {
  if (value === true || value === 1) return true;
  if (value === false || value === 0) return false;
  if (typeof value === "string") {
    const normalized = value.trim().toLowerCase();
    if (normalized === "true" || normalized === "1") return true;
    if (normalized === "false" || normalized === "0") return false;
  }
  return null;
};

const readFlagFromCandidates = (candidates) => {
  for (const value of candidates) {
    const parsed = toPinnedBool(value);
    if (parsed !== null) return parsed;
  }
  return null;
};

/**
 * Author pin lives on the post entity (same for every viewer).
 * postActionsInfo.isPinned is viewer-specific and is often false for non-owners,
 * so it must not override postInfo.isPinned.
 */
export const readGlobalPinnedFlag = (...sources) => {
  for (const source of sources) {
    if (source == null) continue;

    if (typeof source !== "object") {
      const direct = toPinnedBool(source);
      if (direct !== null) return direct;
      continue;
    }

    const flag = readFlagFromCandidates([
      source.isPinned,
      source.pinned,
      source.isPin,
      source.postInfo?.isPinned,
      source.postInfo?.pinned,
      source.postInfo?.isPin,
      source.postInfo?.postInfo?.isPinned,
      source.postInfo?.postInfo?.pinned,
      source.postInfo?.postInfo?.isPin,
    ]);

    if (flag !== null) return flag;
  }

  return null;
};

/**
 * Resolves pinned state from postActionsInfo / post / feed item shapes.
 * Accepts bool, 0/1, and "true"/"false" (matches mobile PostActionsInfo parsing).
 * Returns null when no pin flag is present.
 */
export const readPinnedFlag = (...sources) => {
  for (const source of sources) {
    if (source == null) continue;

    if (typeof source !== "object") {
      const direct = toPinnedBool(source);
      if (direct !== null) return direct;
      continue;
    }

    const flag = readFlagFromCandidates([
      source.isPinned,
      source.pinned,
      source.isPin,
      source.postActionsInfo?.isPinned,
      source.postActionsInfo?.pinned,
      source.postActionsInfo?.isPin,
      source.postInfo?.isPinned,
      source.postInfo?.postActionsInfo?.isPinned,
      source.postInfo?.postActionsInfo?.pinned,
      source.postInfo?.postActionsInfo?.isPin,
      source.postInfo?.postInfo?.isPinned,
      source.postInfo?.postInfo?.postActionsInfo?.isPinned,
    ]);

    if (flag !== null) return flag;
  }

  return null;
};

export const resolveIsPinned = (...sources) => readPinnedFlag(...sources) === true;

export const resolveGlobalPostPinnedState = (...sources) =>
  readGlobalPinnedFlag(...sources) === true;

const PINNED_POSTS_STORAGE_KEY = "wlj_pinnedPostIds";

const canUseLocalStorage = () =>
  typeof localStorage !== "undefined" && localStorage != null;

export const getPinnedPostIds = () => {
  if (!canUseLocalStorage()) return [];
  try {
    const raw = localStorage.getItem(PINNED_POSTS_STORAGE_KEY);
    const parsed = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed) ? parsed.map(String) : [];
  } catch {
    return [];
  }
};

export const isPostPinnedInStorage = (postId) => {
  if (postId == null || postId === "") return false;
  return getPinnedPostIds().includes(String(postId));
};

export const setPostPinnedInStorage = (postId, pinned) => {
  if (!canUseLocalStorage() || postId == null || postId === "") return;
  const id = String(postId);
  const next = new Set(getPinnedPostIds());
  if (pinned) next.add(id);
  else next.delete(id);
  localStorage.setItem(PINNED_POSTS_STORAGE_KEY, JSON.stringify([...next]));
};

const getFeedPostId = (post) => post?.postInfo?.postId ?? post?.postId;

const isFeedPostOwned = (post) =>
  post?.postInfo?.isOwned === true || post?.isOwned === true;

/**
 * Resolves pin state for feed items.
 * - All viewers: post-level isPinned from the API.
 * - Owner only: localStorage fallback when the API omits isPinned (NewsFeeds often does).
 */
export const resolveFeedPostPinnedState = (post) => {
  const postId = getFeedPostId(post);
  if (postId == null) return false;

  const globalFlag = readGlobalPinnedFlag(post?.postInfo, post);
  if (globalFlag === true) return true;
  if (isFeedPostOwned(post) && isPostPinnedInStorage(postId)) return true;
  if (globalFlag === false) return false;
  return false;
};

/**
 * Resolves pin state for UI. Prefer post-level API data, then owner localStorage.
 * Do not pass boolean props (e.g. ispinned={false}) as sources — they block storage fallback.
 */
export const resolvePostPinnedState = (postId, ...sources) => {
  const objectSources = sources.filter(
    (source) => source != null && typeof source === "object"
  );
  const globalFlag = readGlobalPinnedFlag(...objectSources);
  if (globalFlag === true) return true;
  if (isPostPinnedInStorage(postId)) return true;
  if (globalFlag === false) return false;
  return false;
};

/**
 * Reads pin status from GetPostInfo (global post fields, then owner postActionsInfo).
 */
export const readPinnedFlagFromPostInfo = (postInfo) => {
  const globalFlag = readGlobalPinnedFlag(postInfo);
  if (globalFlag !== null) return globalFlag;
  return readPinnedFlag(postInfo?.postActionsInfo, postInfo);
};

/**
 * Keeps pinned posts at the top of the feed (stable order within each group).
 */
export const sortPinnedPostsFirst = (list = []) => {
  if (!Array.isArray(list) || list.length < 2) return list || [];

  const pinned = [];
  const unpinned = [];

  list.forEach((post) => {
    if (resolveFeedPostPinnedState(post)) pinned.push(post);
    else unpinned.push(post);
  });

  if (pinned.length === 0) return list;
  return [...pinned, ...unpinned];
};

/**
 * Updates isPinned on a feed item and optionally moves a pinned post to the top.
 */
export const applyPostPinnedState = (prevList = [], postId, pinned) => {
  const updated = prevList.map((post) => {
    if (String(post?.postInfo?.postId) !== String(postId)) return post;
    return {
      ...post,
      postInfo: {
        ...post.postInfo,
        isPinned: pinned,
        postActionsInfo: {
          ...post.postInfo?.postActionsInfo,
          isPinned: pinned,
        },
      },
    };
  });

  if (!pinned) return sortPinnedPostsFirst(updated);

  const pinnedPost = updated.find(
    (post) => String(post?.postInfo?.postId) === String(postId)
  );
  if (!pinnedPost) return updated;

  return [
    pinnedPost,
    ...updated.filter(
      (post) => String(post?.postInfo?.postId) !== String(postId)
    ),
  ];
};

export const extractPostInfoFromGetPostResponse = (responseData) => {
  if (!responseData) return null;
  if (responseData?.postInfo?.postInfo?.postId != null) {
    return responseData.postInfo.postInfo;
  }
  if (responseData?.postInfo?.postId != null) {
    return responseData.postInfo;
  }
  if (responseData?.postId != null) {
    return responseData;
  }
  return responseData?.postInfo || responseData;
};

/**
 * Normalizes a post from GetPostInfo (or similar) into the NewsFeeds dataList item shape.
 */
export const normalizeNewsFeedPost = (rawPost, sortTypePhase = 1) => {
  if (!rawPost) return null;

  const hasNestedPostInfo =
    rawPost.postInfo?.postId == null &&
    rawPost.postInfo?.postInfo?.postId != null;

  if (hasNestedPostInfo) {
    const { postInfo: nested, ...rest } = rawPost;
    return {
      ...rest,
      postInfo: nested.postInfo,
      __sortTypePhase: sortTypePhase,
    };
  }

  if (rawPost.postId != null && rawPost.postInfo == null) {
    return {
      postInfo: rawPost,
      __sortTypePhase: sortTypePhase,
    };
  }

  return {
    ...rawPost,
    __sortTypePhase: rawPost.__sortTypePhase ?? sortTypePhase,
  };
};

export const prependNewsFeedPost = (prevList, rawPost, sortTypePhase = 1) => {
  const normalized = normalizeNewsFeedPost(rawPost, sortTypePhase);
  if (!normalized?.postInfo?.postId) return prevList || [];

  const postId = normalized.postInfo.postId;
  const filtered = (prevList || []).filter(
    (item) => item?.postInfo?.postId !== postId
  );
  return [normalized, ...filtered];
};

/**
 * Turns a GetPostInfo response into the NewsFeeds card shape used by
 * PostComponent / PostEngagement / PostInteractionModal.
 */
export const toNewsFeedItemFromGetPostInfo = (responseData) => {
  if (!responseData) return null;

  const nestedWrapper =
    responseData.postInfo?.postId == null &&
    responseData.postInfo?.postInfo?.postId != null
      ? {
          ...responseData,
          ...responseData.postInfo,
          postInfo: responseData.postInfo.postInfo,
        }
      : responseData;

  const normalized = normalizeNewsFeedPost(nestedWrapper);
  const postInfo =
    normalized?.postInfo ||
    extractPostInfoFromGetPostResponse(nestedWrapper) ||
    extractPostInfoFromGetPostResponse(responseData);

  if (!postInfo?.postId) return null;

  const cntntTypCategory =
    normalized?.cntntTypCategory ||
    nestedWrapper?.cntntTypCategory ||
    responseData?.cntntTypCategory ||
    postInfo.cntntTypCategory ||
    postInfo.postCategory ||
    normalized?.contentType ||
    postInfo.contentType;

  const isFollowedByYou =
    postInfo.isFollowedByYou ??
    postInfo.postActionsInfo?.isFollowed ??
    nestedWrapper?.isFollowedByYou ??
    false;

  return {
    ...(normalized || {}),
    postInfo: {
      ...postInfo,
      isFollowedByYou,
    },
    cntntTypCategory,
    contentType:
      normalized?.contentType ||
      nestedWrapper?.contentType ||
      postInfo.contentType ||
      postInfo.postCategory ||
      cntntTypCategory,
  };
};
